import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useReportAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);

  // Fetch all reports
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.reports());
      const raw = response.data?.data ?? (Array.isArray(response.data) ? response.data : []);
      setReports(raw);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching reports:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch report by ID
  const fetchReportById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.reportById(id));
      setCurrentReport(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching report:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new report
  const createReport = useCallback(async (reportData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        apiUrl.createReport(),
        reportData
      );
      setReports((prevReports) => [...prevReports, response.data]);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error creating report:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update report
  const updateReport = useCallback(async (id, reportData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.put(
        apiUrl.updateReport(id),
        reportData
      );
      setReports((prevReports) =>
        prevReports.map((r) => (r.id === id ? response.data : r))
      );
      if (currentReport?.id === id) {
        setCurrentReport(response.data);
      }
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating report:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentReport]);

  // Delete report
  const deleteReport = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.delete(apiUrl.deleteReport(id));
      setReports((prevReports) =>
        prevReports.filter((r) => r.id !== id)
      );
      if (currentReport?.id === id) {
        setCurrentReport(null);
      }
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error deleting report:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentReport]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear current report
  const clearCurrentReport = useCallback(() => {
    setCurrentReport(null);
  }, []);

  return {
    loading,
    error,
    reports,
    currentReport,
    fetchReports,
    fetchReportById,
    createReport,
    updateReport,
    deleteReport,
    clearError,
    clearCurrentReport,
  };
};

export default useReportAdmin;
